import React, { forwardRef } from 'react';
import * as S from './navigation.styles';
import {
  useInteractiveElement,
  useItemIconColor,
  useNavigationState,
  useNavItemState,
} from '@design-system/molecules/navigation/navigation.hooks';
import {
  NavigationHeaderProps,
  NavigationItemProps,
  NavigationSubItemProps,
  NavigationSubItemsProps,
} from './navigation.types';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@design-system/atoms/icon/Icon';
import { Tooltip } from '@design-system/atoms/tooltip';
import { ActionMenu } from '../action-menu';
import { NavigationContext, useNavigationContext } from './navigation.context';
import { appConfig } from 'src/config';
import { IconButton } from '@design-system/atoms/icon-button';
import { Icon as IconType } from '@phosphor-icons/react';

// Main Navigation component
const NavigationRoot: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const {
    expandedItems,
    activeItem,
    isNavigationCollapsed,
    sidebarWidth,
    sidebarRef,
    toggleItemExpand,
    setActiveMainItem,
    setActiveSubItem,
    clearExpandedItems,
    toggleSidebar,
  } = useNavigationState();

  return (
    <NavigationContext.Provider
      value={{
        expandedItems,
        clearExpandedItems,
        toggleItemExpand,
        activeItem,
        setActiveMainItem,
        setActiveSubItem,
        isNavigationCollapsed,
        toggleSidebar,
      }}
    >
      <S.SidebarContainer
        ref={sidebarRef}
        animate={{ width: `${sidebarWidth}px` }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {children}
        <ResizeButton />
      </S.SidebarContainer>
    </NavigationContext.Provider>
  );
};

const LineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M5.625 2.81238C5.37636 2.81238 5.1379 2.91115 4.96209 3.08697C4.78627 3.26278 4.6875 3.50124 4.6875 3.74988V16.2499C4.6875 16.4985 4.78627 16.737 4.96209 16.9128C5.1379 17.0886 5.37636 17.1874 5.625 17.1874C5.87364 17.1874 6.1121 17.0886 6.28791 16.9128C6.46373 16.737 6.5625 16.4985 6.5625 16.2499V3.74988C6.5625 3.50124 6.46373 3.26278 6.28791 3.08697C6.1121 2.91115 5.87364 2.81238 5.625 2.81238Z"
      fill="#040404"
      fillOpacity="0.3"
    />
  </svg>
);

const ResizeButton = () => {
  const { isNavigationCollapsed, toggleSidebar } = useNavigationContext();

  return (
    <S.ResizeButtonContainer>
      <S.HoverButton>
        <S.ResizeButton
          size={20}
          $isNavigationCollapsed={isNavigationCollapsed}
          onPress={toggleSidebar}
          icon="dropdown"
        />
      </S.HoverButton>
      <S.DefaultButton>
        <IconButton icon={LineIcon as unknown as IconType} />
      </S.DefaultButton>
    </S.ResizeButtonContainer>
  );
};

// Header component
const Header: React.FC<NavigationHeaderProps> = ({ logo }) => {
  const { isNavigationCollapsed } = useNavigationContext();
  const logoSrc = isNavigationCollapsed ? undefined : logo?.regular;

  return (
    <S.LogoContainer $isNavigationCollapsed={isNavigationCollapsed}>
      {logoSrc && <img src={logoSrc} alt="logo" />}
    </S.LogoContainer>
  );
};

// User section component
const User: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { isNavigationCollapsed } = useNavigationContext();
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray.length > 0 ? childrenArray[0] : null;

  return (
    <S.UserSection $isNavigationCollapsed={isNavigationCollapsed}>
      {firstChild}
      {!isNavigationCollapsed ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2, ease: 'easeInOut' }}
        >
          {childrenArray.slice(1)}
        </motion.div>
      ) : null}
    </S.UserSection>
  );
};

// Navigation items component
const Items: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  return <S.NavSection>{children}</S.NavSection>;
};

// Navigation item component
const Item: React.FC<NavigationItemProps> = (props) => {
  const { id, icon, label, subItems, isActive, onPress, badge } = props;

  const {
    shouldHighlight,
    hasSubItems,
    isNavigationCollapsed,
    isExpanded,
    isItemActive,
    handleItemPress,
    showTooltip,
  } = useNavItemState({ id, subItems, onPress, isActive });

  // Define the color of the icon based on the active state
  const { color, setIsHovered } = useItemIconColor({
    isActive: isItemActive,
    shouldHighlight,
  });

  const { elementProps, ref, isFocusVisible } = useInteractiveElement(handleItemPress);

  const formatBadgeContent = (value?: number) => {
    if (!value || value <= 0) return null;
    return value > 99 ? '99+' : value;
  };
  const badgeContent = formatBadgeContent(Number(badge));

  return (
    <S.NavItemContainer $isNavigationCollapsed={isNavigationCollapsed}>
      <Tooltip content={label.toUpperCase()} isEnabled={showTooltip}>
        <S.NavItem
          ref={ref}
          shouldHighlight={shouldHighlight}
          isFocusVisible={isFocusVisible}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          $isNavigationCollapsed={isNavigationCollapsed}
          {...elementProps}
          aria-expanded={!isNavigationCollapsed ? isExpanded : undefined}
          data-testid={`nav-item-${label}`}
        >
          {icon && (
            <>
              <Icon name={icon} color={color} size={24} />
              {isNavigationCollapsed && Number(badge) > 0 && (
                <S.NavItemBadge
                  data-testid={`nav-item-${label}-badge`}
                  style={{ position: 'absolute', top: 4, right: 4 }}
                >
                  {badgeContent}
                </S.NavItemBadge>
              )}
            </>
          )}
          {!isNavigationCollapsed && (
            <S.NavItemLabel shouldHighlight={shouldHighlight} variant="body-16" style={{ display: 'flex' }}>
              {label}
              {!!badge && <S.NavItemBadge data-testid={`nav-item-${label}-badge`}>{badgeContent}</S.NavItemBadge>}
            </S.NavItemLabel>
          )}
          {!isNavigationCollapsed && hasSubItems && (
            <motion.div
              animate={isExpanded ? 'expanded' : 'collapsed'}
              variants={{
                collapsed: { rotate: 0 },
                expanded: { rotateX: 180, transition: { duration: 0.3 } },
              }}
            >
              <Icon name="dropdown" color={color} size={16} />
            </motion.div>
          )}
        </S.NavItem>
      </Tooltip>
      {subItems?.length ? <Navigation.SubItems ref={ref} subItems={subItems} id={id} label={label} /> : null}
    </S.NavItemContainer>
  );
};

// Sub items component
const SubItems = forwardRef<HTMLElement, NavigationSubItemsProps>((props, ref) => {
  const { subItems, id, label } = props;

  const { activeItem, toggleItemExpand, expandedItems, isNavigationCollapsed, setActiveSubItem } =
    useNavigationContext();

  const isExpanded = Boolean(expandedItems[id]);

  const handlePress = (subItemId: string) => {
    setActiveSubItem(id, subItemId);
  };

  return !isNavigationCollapsed ? (
    <AnimatePresence>
      {isExpanded ? (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <S.SubItemsContainer>
            {subItems.map((item, index) => (
              <Navigation.SubItem
                key={item.id || index}
                item={item}
                isActive={item.id === activeItem.subItemId}
                data-testid={`nav-subitem-${item.label}`}
                onPress={() => {
                  handlePress(item.id);
                  item.onPress?.();
                }}
              />
            ))}
          </S.SubItemsContainer>
        </motion.div>
      ) : null}
    </AnimatePresence>
  ) : (
    <ActionMenu
      title={label.toUpperCase()}
      triggerRef={ref as React.RefObject<HTMLElement>}
      isOpen={isExpanded}
      onClose={() => toggleItemExpand(id)}
    >
      {subItems.map((item) => {
        return (
          <ActionMenu.Item
            {...item}
            key={item.id}
            isActive={item.id === activeItem.subItemId}
            onPress={() => {
              handlePress(item.id);
              item.onPress?.();
            }}
          />
        );
      })}
    </ActionMenu>
  );
});

// Sub item component
const SubItem: React.FC<NavigationSubItemProps> = (props) => {
  const { item, isActive = false, onPress } = props;
  const { elementProps, ref, isFocusVisible } = useInteractiveElement(onPress);

  return (
    <S.SubItem ref={ref} shouldHighlight={isActive} isFocusVisible={isFocusVisible} {...elementProps}>
      <S.SubItemLabel shouldHighlight={isActive}>{item.label}</S.SubItemLabel>
    </S.SubItem>
  );
};

// Footer component
const Footer: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { isNavigationCollapsed } = useNavigationContext();

  // If navigation is collapsed, don't render the footer at all
  if (isNavigationCollapsed) {
    return null;
  }

  // Only animate opacity when showing the footer
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.2, ease: 'easeInOut' }}
    >
      <S.SidebarFooter>
        {children}
        <S.Version>{`© ${new Date().getFullYear()} | ${appConfig.REACT_APP_VERSION}`}</S.Version>
      </S.SidebarFooter>
    </motion.div>
  );
};

// Compose the compound component
export const Navigation = Object.assign(NavigationRoot, {
  Header,
  User,
  Items,
  Item,
  SubItems,
  SubItem,
  Footer,
});
